// FAQ.js
import React, { useState } from 'react';
import { Plus, Minus, Phone, ArrowRight } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What services does B & N Enterprise provide?",
      answer: "We offer a broad range of in-home care services, including Alzheimer's and Dementia Care, Companionship Care, Hospice Care, Personal Care, Post-Operative Care, Respite Care, Specialty Care, and Spinal Cord Injury Care. Every service is shaped around the needs of the people we support and their families."
    },
    {
      question: "How do I get started with care services?",
      answer: "Getting started is simple! Call our office at +61 480 804 640 to speak with our friendly team. We'll schedule a complimentary in-home assessment to understand your specific needs and create a customised care plan. From there, we'll match you with a qualified carer who fits your requirements and preferences."
    },
    {
      question: "Are your carers qualified and insured?",
      answer: "Absolutely. All our carers are thoroughly trained, certified, and insured. They undergo comprehensive background checks and receive ongoing training to ensure they provide the highest quality of care. Our team is equipped to handle a wide range of care needs with professionalism and compassion."
    },
    {
      question: "What areas do you serve?",
      answer: "We are proud to support families across Canberra and the wider ACT, including Weston and the surrounding suburbs. Our aim is to bring quality in-home care to our community, so older Australians can stay comfortable and safe in their own homes."
    },
    {
      question: "Can I choose my carer?",
      answer: "Yes! We believe the relationship between carer and client is crucial to quality care. During your assessment, we'll discuss your preferences and match you with a carer whose personality, skills, and experience align with your needs. If for any reason the match isn't perfect, we'll work with you to find a better fit."
    },
    {
      question: "What is the cost of care services?",
      answer: "Care costs vary depending on the type and frequency of services needed. During your complimentary in-home assessment, we'll provide a transparent breakdown of costs based on your customised care plan. We're committed to working with families to find affordable solutions that meet their needs."
    },
    {
      question: "Do you offer 24/7 care?",
      answer: "Yes, we offer flexible care schedules ranging from a few hours per week to around-the-clock care, depending on your needs. Whether you need daily assistance, overnight care, or live-in support, we can create a schedule that works for you and your family."
    },
    {
      question: "What is respite care and how does it work?",
      answer: "Respite care provides temporary relief for family carers who need a break to rest, recharge, or attend to personal matters. Our trained carers step in to provide the same quality care your loved one receives, whether it's for a few hours, a day, or several days. This allows you to take time for yourself knowing your loved one is in safe, compassionate hands."
    },
    {
      question: "How do you ensure the safety of clients?",
      answer: "Safety is our top priority. Our carers are trained in fall prevention, emergency response, and proper transfer techniques. We conduct regular safety assessments of the home environment and maintain open communication with families and healthcare professionals. All our staff undergo rigorous background checks and are bonded and insured."
    },
    {
      question: "Can you work with my loved one's existing healthcare team?",
      answer: "Absolutely! We believe in collaborative care and work closely with doctors, nurses, therapists, and other healthcare professionals involved in your loved one's care. Our carers can attend medical appointments, communicate important health updates, and ensure care plans are followed consistently."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Intro band */}
      <section className="bg-bnCream pt-16 pb-16 md:pt-24 md:pb-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Frequently Asked Questions
          </p>
          <h1 className="font-display font-light text-bnInk leading-[1.1] text-4xl md:text-5xl mb-5">
            Frequently Asked Questions
          </h1>
          <p className="text-bnBody text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Find answers to common questions about our care services, carers, and how we can support you and your loved ones.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div>
            {faqs.map((faq, index) => {
              const open = openIndex === index;
              return (
                <div key={index} className="border-b border-bnBorder">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center gap-3 text-left py-5"
                  >
                    <span className="text-bnAccent shrink-0">
                      {open ? <Minus size={20} /> : <Plus size={20} />}
                    </span>
                    <h3 className={`text-lg font-medium transition-colors ${open ? 'text-bnAccent' : 'text-bnInk'}`}>
                      {faq.question}
                    </h3>
                  </button>
                  {open && (
                    <div className="pb-6 pl-8 pr-2">
                      <p className="text-bnBody text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="bg-bnCream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            We Are Here to Help
          </p>
          <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl mb-5">
            Still Have Questions?
          </h2>
          <p className="text-bnBody text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Our friendly team is here to help. Give us a call or visit our contact page to get in touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+61480804640"
              className="inline-flex items-center justify-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
            >
              <Phone size={16} />
              Call +61 480 804 640
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 border border-bnBorder text-bnInk hover:border-bnAccent hover:text-bnAccent font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
            >
              Contact Us
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
